import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { useApi } from 'app/shared/contexts/api';
import { useOpenParams } from 'app/shared/hooks/use-open-params';
import { BOOKING_QUERY_KEY } from 'app/data/queries/use-booking-query';
import { ACTIVITY_FEED_QUERY_KEY } from 'app/data/queries/use-activity-feed-query';

/** Wire value for a cancellation (resource-booking `PATCH_STATUSES.CANCELLED`). */
const CANCELLED_STATUS = 'cancelled';

interface CancelBody {
  status: string;
}

/**
 * Cancel a resource booking. `PATCH /api/transactions/{numeric id}` with `{ status: 'cancelled' }` —
 * NOTE: the endpoint keys on the transaction's NUMERIC `id`, not its uuid, and lives under
 * `/api/transactions` (not the `/api/tenant/v1` prefix). On success, invalidate the booking + the
 * activity feed so both reflect the cancellation.
 */
export const useCancelBookingMutation = (): UseMutationResult<void, Error, number> => {
  const { apiUrl, getAuthHeaders, getDefaultHeaders } = useApi();
  const { buildingUuid } = useOpenParams();
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (bookingId: number) => {
      const headers = { ...(await getAuthHeaders()), ...getDefaultHeaders(buildingUuid) };
      await apiRequest<void, CancelBody, void>({
        url: `${apiUrl}/api/transactions/${bookingId}`,
        method: HttpMethod.PATCH,
        headers,
        body: { status: CANCELLED_STATUS },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOOKING_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACTIVITY_FEED_QUERY_KEY] });
    },
  });
};
